import { BALL_VALUES, CLEARANCE_ORDER, COLOR_POINTS_TOTAL, TOTAL_REDS, } from "./balls.js";
import { MAX_SCORE_ADDITION } from "./events.js";
/**
 * Where a frame opens, given a handicap.
 *
 * The player giving the points away starts at nought and their opponent
 * starts at the handicap: "A gives 20" means A 0, B 20. The points are put on
 * the board rather than taken off the giver, because a score cannot go below
 * nought and because what a spectator has to be able to do is read the two
 * numbers and see who is ahead.
 */
export function handicapScores(handicap) {
    if (!handicap)
        return [0, 0];
    return handicap.from === 0 ? [0, handicap.points] : [handicap.points, 0];
}
/** Frames needed to win a best-of-N. Best-of-1 -> 1, best-of-9 -> 5. */
export function framesToWin(bestOf) {
    return Math.floor(bestOf / 2) + 1;
}
/**
 * When the match must be over, or `null` when it is untimed or not started.
 *
 * Derived rather than stored so that every device computes the same instant
 * from the same event log, however late it joined.
 */
export function matchDeadline(s) {
    if (s.startedAt === null || s.timeLimitMinutes === null)
        return null;
    return new Date(new Date(s.startedAt).getTime() + s.timeLimitMinutes * 60_000 + (s.idleDurationMs ?? 0));
}
export function initialState(matchId) {
    return {
        matchId,
        players: ['Player 1', 'Player 2'],
        playerCountries: [null, null],
        scores: [0, 0],
        framesWon: [0, 0],
        bestOf: 1,
        redCount: TOTAL_REDS,
        framesToWin: 1,
        timeLimitMinutes: null,
        handicap: null,
        startedAt: null,
        idleDurationMs: 0,
        idleClosure: null,
        frameResults: [],
        visits: [],
        frameNumber: 1,
        activePlayer: 0,
        currentBreak: 0,
        highestBreak: [0, 0],
        redsRemaining: TOTAL_REDS,
        colorsRemaining: [...CLEARANCE_ORDER],
        mustPotColor: false,
        freeBall: false,
        freeBallDecisionPending: false,
        paused: false,
        resetOverlay: false,
        pauseReason: null,
        pauseMessage: null,
        target: 'red',
        pointsAvailable: TOTAL_REDS * 8 + COLOR_POINTS_TOTAL,
        frameDecided: false,
        projectedWinner: null,
        matchWinner: null,
        // AI inference is opt-in. The operator enables it from the match control
        // bar, which changes this to `assisted`.
        mode: 'manual',
        streaming: true,
        status: 'idle',
        lastSeq: -1,
    };
}
function opponent(p) {
    return p === 0 ? 1 : 0;
}
/**
 * The visit this player is currently making, opening one if they have just
 * come to the table.
 *
 * Copies before it mutates: visits are shared with the previous state, and
 * `applyEvent` promises not to touch it.
 */
function openVisit(s, p) {
    const last = s.visits[s.visits.length - 1];
    if (last !== undefined && !last.ended && last.player === p) {
        const reopened = { ...last, entries: [...last.entries] };
        s.visits = [...s.visits.slice(0, -1), reopened];
        return reopened;
    }
    const started = { player: p, entries: [], points: 0, ended: false };
    s.visits = [...s.visits, started];
    return started;
}
/** Close the open visit, if there is one. An empty visit is dropped. */
function closeVisit(s) {
    const last = s.visits[s.visits.length - 1];
    if (last === undefined || last.ended)
        return;
    // A player who came to the table and did nothing recordable leaves no
    // trace, rather than an empty box with a zero in it.
    if (last.entries.length === 0) {
        s.visits = s.visits.slice(0, -1);
        return;
    }
    s.visits = [...s.visits.slice(0, -1), { ...last, ended: true }];
}
/**
 * Points still on the table.
 *
 * Per `docs/frontend-backend-contract.md`:
 *  - reds remain  -> redsRemaining * 8 + 27  (each red plus a black, then the colours)
 *  - no reds left -> the sum of the colours still to be cleared
 */
function pointsAvailable(s) {
    const onTable = s.redsRemaining > 0
        ? s.redsRemaining * 8 + COLOR_POINTS_TOTAL
        : s.colorsRemaining.reduce((sum, c) => sum + BALL_VALUES[c], 0);
    if (!s.freeBall)
        return onTable;
    // A free ball is worth points that are not on the table, which is the whole
    // reason the maximum break is 155 rather than 147. It has to be counted or
    // `frameDecided` will call a frame over while the player still behind can
    // in fact catch up — the one arithmetic error on this board that would end
    // a frame somebody was still winning.
    if (s.target === 'red') {
        // The nominated ball scores one and no red leaves the table, so the
        // striker gains a whole extra red-and-a-colour cycle.
        return onTable + BALL_VALUES.red + BALL_VALUES.black;
    }
    // While a colour is owed a free ball is worth nothing extra: see
    // `illegalPotReason`, where nominating one is refused because tapping the
    // colour itself already produces the identical score and the identical
    // table.
    if (s.target === 'color')
        return onTable;
    // Clearance: the nominated ball scores the ball on, and the ball on is
    // still there to be potted afterwards.
    return onTable + BALL_VALUES[s.target];
}
/**
 * What a nominated free ball is worth: the value of the ball on.
 *
 * This is the whole of the free ball rule that a scoreboard has to know. The
 * ball that goes down is not what is scored — nominate the black while the
 * reds are up and it is worth one, nominate a red while the yellow is on and
 * it is worth two. Reading the value off the ball that was potted is the
 * mistake this function exists to prevent.
 */
export function freeBallValue(s, nominated) {
    if (s.target === 'red')
        return BALL_VALUES.red;
    if (s.target === 'color')
        return BALL_VALUES[nominated];
    return BALL_VALUES[s.target];
}
/**
 * Whether the board should be offering a free ball to the player at the table.
 *
 * A free ball follows a foul, but not every foul: it is offered to the player
 * the table was handed *to*, before they have played a stroke, and never to
 * the offender who has been made to play again. Deriving it from the visit log
 * rather than from a flag means it survives undo — retract the foul and the
 * offer goes with it — and that the phone and the board agree without either
 * of them remembering anything.
 *
 * Once armed it keeps saying yes, so the control that turned it on is still
 * there to turn it off.
 */
export function freeBallOffered(s) {
    if (s.status !== 'in_progress')
        return false;
    return s.freeBallDecisionPending || s.freeBall;
}
/** Whether this ball physically still exists on the table right now. */
export function isBallOnTable(s, color) {
    if (color === 'red')
        return s.redsRemaining > 0;
    return s.colorsRemaining.includes(color);
}
/**
 * Whether tapping this ball now would be a free ball rather than an ordinary
 * pot.
 *
 * The distinction is derived rather than asked of the operator: a ball that
 * is legal anyway is potted normally, and the free ball is spent only on a
 * ball that would otherwise be refused. That is also what makes the free ball
 * safe to leave armed — arming it can never change what an ordinary pot is
 * worth.
 */
export function isFreeBallPot(s, color) {
    if (!s.freeBall || s.status !== 'in_progress')
        return false;
    return illegalPotReason(s, color, 1) !== null && illegalPotReason(s, color, 1, true) === null;
}
function currentTarget(s) {
    if (s.mustPotColor)
        return 'color';
    if (s.redsRemaining > 0)
        return 'red';
    return s.colorsRemaining[0] ?? 'black';
}
/**
 * Why this pot is not legal, or `null` if it is.
 *
 * Snooker has an order: red, colour, red, colour, until the reds are gone,
 * and then the colours in ascending value. Scoring a pot that breaks that
 * order is not a difference of opinion about the rules, it is a mis-tap —
 * nobody pots two colours in a row and nobody clears the pink before the
 * blue. Letting it through corrupts every number downstream of it: the
 * points remaining, the break, the frame projection and the statistics.
 *
 * A mis-tap that *is* recorded is fixed with Undo, which is one button. A
 * mis-tap recorded as a legal pot is fixed by nobody, because by the time
 * anyone notices, the score is simply wrong and the log says otherwise.
 *
 * The message is returned rather than a bare boolean so the server can say
 * what was wrong instead of refusing silently.
 */
export function illegalPotReason(s, color, count, freeBall = false) {
    if (s.status !== 'in_progress')
        return `the frame is ${s.status}`;
    if (s.paused)
        return 'table is paused';
    if (!Number.isInteger(count) || count < 1)
        return 'count must be a positive integer';
    if (s.practice && !freeBall)
        return color === 'red' || count === 1 ? null : 'only reds may be potted more than once';
    if (freeBall) {
        if (!s.freeBall)
            return 'no free ball has been awarded';
        if (count !== 1)
            return 'a free ball is a single nominated ball';
        // A free ball can only be nominated from balls that physically exist on
        // the table. In the colours phase there are no reds at all, and a colour
        // already cleared is off the table for good.
        if (color === 'red' && s.redsRemaining === 0)
            return 'no reds remain';
        if (color !== 'red' && !s.colorsRemaining.includes(color))
            return `the ${color} is not on`;
        // The ball on is not a free ball. Potting it is the shot the striker was
        // entitled to all along, and recording it as a nomination would respot a
        // ball that should have stayed down.
        if (s.target === color)
            return `the ${color} is the ball on, not a free ball`;
        // A colour is owed. Nominating a red here would score the value of
        // whichever colour the striker declared as the ball on, and one tap
        // cannot say both which ball went down and which colour was declared.
        // Nothing is lost by refusing it: potting the declared colour directly
        // scores exactly the same and leaves exactly the same table, because a
        // colour potted while reds remain is respotted either way.
        if (s.target === 'color')
            return 'a colour is owed; pot the colour itself';
        return null;
    }
    if (color === 'red') {
        // A red is legal whenever a red is on the table. That includes straight
        // after another red: potting two reds in one stroke is recorded as two
        // taps on the red, which is why there is no "2 reds" button any more.
        // The player still owes a colour either way — `mustPotColor` stays set —
        // so the order is not being relaxed, only the way it is entered.
        if (s.redsRemaining === 0)
            return 'no reds remain';
        if (count > s.redsRemaining) {
            return `only ${s.redsRemaining} red${s.redsRemaining === 1 ? '' : 's'} remain`;
        }
        return null;
    }
    // Only reds are potted more than one at a time.
    if (count !== 1)
        return 'only reds can be potted more than one at a time';
    if (s.target === 'red')
        return 'a red is on';
    // Straight after a red, any colour may be nominated.
    if (s.target === 'color')
        return null;
    // Clearance: the colours go in ascending order of value.
    if (s.target !== color)
        return `the ${s.target} is on`;
    return null;
}
/** Whether this pot follows the order of the game. */
export function isLegalPot(s, color, count = 1, freeBall = false) {
    return illegalPotReason(s, color, count, freeBall) === null;
}
export function illegalRedAdjustmentReason(s, delta) {
    if (delta !== -1 && delta !== 1)
        return 'red adjustment must be -1 or 1';
    if (s.practice)
        return 'red adjustments are not available in practice';
    if (s.status !== 'in_progress')
        return `the frame is ${s.status}`;
    if (s.paused)
        return 'table is paused; resume before adjusting reds';
    if (s.redsRemaining + delta < 0)
        return 'no reds remain';
    if (s.redsRemaining + delta > TOTAL_REDS)
        return 'at most 15 reds can be on the table';
    if (delta > 0 && s.colorsRemaining.length < CLEARANCE_ORDER.length) {
        return 'undo colour clearance before restoring reds';
    }
    return null;
}
export function illegalScoreAdditionReason(s, player, points, maximum = MAX_SCORE_ADDITION) {
    if (s.practice)
        return 'bulk scoring is not available in practice';
    if (s.status !== 'in_progress')
        return `the frame is ${s.status}`;
    if (s.paused)
        return 'table is paused; resume before scoring';
    if (!Number.isInteger(points) || points < 1 || points > maximum) {
        return `points must be an integer between 1 and ${maximum}`;
    }
    if (!Number.isSafeInteger(s.scores[player] + points))
        return 'score is too large';
    return null;
}
/**
 * Whether this ball can be tapped at all: legal in its own right, or legal as
 * a nominated free ball. What the ball pad lights up.
 */
export function canPot(s, color, count = 1) {
    if (count === 1 && !isBallOnTable(s, color)) {
        return false;
    }
    return isLegalPot(s, color, count) || (count === 1 && isFreeBallPot(s, color));
}
/** Recompute the fields derived from the rest of the state. */
function derive(s) {
    if (s.idleClosure)
        return s;
    s.target = currentTarget(s);
    s.pointsAvailable = pointsAvailable(s);
    if (s.practice && s.status === 'in_progress') {
        s.activePlayer = 0;
        s.frameDecided = false;
        s.projectedWinner = null;
        return s;
    }
    if (s.status !== 'in_progress') {
        // A finished frame is not "decided", it is over. Keeping these false
        // stops the UI offering to end a frame twice.
        s.frameDecided = false;
        s.projectedWinner = null;
        // Nothing is left to nominate, so a free ball cannot survive the frame
        // it was awarded in. Cleared here rather than in each of the events that
        // can end one, so no future ending can forget.
        s.freeBall = false;
        s.freeBallDecisionPending = false;
        s.paused = false;
        s.resetOverlay = false;
        s.pauseReason = null;
        s.pauseMessage = null;
        s.pointsAvailable = pointsAvailable(s);
        return s;
    }
    const lead = s.scores[0] - s.scores[1];
    const ballsLeft = s.redsRemaining > 0 || s.colorsRemaining.length > 0;
    if (!ballsLeft) {
        // Table cleared. A dead heat means a respotted black, which only a human
        // can resolve, so no winner is projected.
        s.frameDecided = lead !== 0;
        s.projectedWinner = lead === 0 ? null : lead > 0 ? 0 : 1;
    }
    else if (Math.abs(lead) > s.pointsAvailable) {
        s.frameDecided = true;
        s.projectedWinner = lead > 0 ? 0 : 1;
    }
    else {
        s.frameDecided = false;
        s.projectedWinner = null;
    }
    return s;
}
/**
 * The `frame.started` event for the next frame.
 *
 * The break alternates every frame; frame 1 is broken by player 0, so the
 * breaker is derived from the frame number rather than tracked separately.
 */
export function nextFrameStart(s) {
    const frameNumber = s.frameNumber + 1;
    return { frameNumber, startingPlayer: ((frameNumber - 1) % 2) };
}
function endBreak(s) {
    const p = s.activePlayer;
    // Normally redundant — `shot.pot` already raises the mark as the break
    // grows, so the readout climbs live rather than jumping when the player
    // misses. This is the backstop for any path that adds to a break without
    // going through a pot.
    if (s.currentBreak > s.highestBreak[p]) {
        s.highestBreak[p] = s.currentBreak;
    }
    s.currentBreak = 0;
    closeVisit(s);
}
function switchTurn(s) {
    endBreak(s);
    s.activePlayer = opponent(s.activePlayer);
    s.mustPotColor = false;
    s.freeBall = false;
    s.freeBallDecisionPending = false;
}
function resetFrame(s, startingPlayer) {
    // Not [0, 0]: a handicapped frame opens with the spotted points already on
    // the board, so the receiver is ahead before the first break-off. They are
    // set here rather than added by an event because they are not a break, must
    // not count towards anybody's statistics, and have to be there again at the
    // start of every frame.
    s.scores = handicapScores(s.handicap);
    s.currentBreak = 0;
    s.visits = [];
    s.redsRemaining = s.redCount ?? TOTAL_REDS;
    s.colorsRemaining = [...CLEARANCE_ORDER];
    s.mustPotColor = false;
    s.freeBall = false;
    s.freeBallDecisionPending = false;
    s.paused = false;
    s.resetOverlay = false;
    s.pauseReason = null;
    s.pauseMessage = null;
    s.activePlayer = startingPlayer;
    s.matchWinner = null;
    s.status = 'in_progress';
}
/**
 * Apply a single event. Pure: returns a new state, never mutates the input.
 * Unknown or impossible events are ignored rather than thrown, so that one
 * bad recogniser event cannot break the whole replay.
 */
export function applyEvent(prev, event) {
    const s = {
        ...prev,
        scores: [...prev.scores],
        framesWon: [...prev.framesWon],
        highestBreak: [...prev.highestBreak],
        colorsRemaining: [...prev.colorsRemaining],
        players: [...prev.players],
        playerCountries: [...prev.playerCountries],
        frameResults: [...prev.frameResults],
        visits: [...prev.visits],
    };
    s.lastSeq = event.seq;
    if (s.idleClosure && event.type !== 'match.resumed' && event.type !== 'match.ended')
        return s;
    switch (event.type) {
        case 'match.started': {
            if (event.practice)
                s.practice = true;
            s.players = [...event.players];
            s.playerCountries = [...(event.countries ?? [null, null])];
            s.bestOf = event.bestOf;
            s.redCount = event.redCount ?? TOTAL_REDS;
            s.redsRemaining = s.redCount;
            s.framesToWin = framesToWin(event.bestOf);
            s.timeLimitMinutes = event.timeLimitMinutes;
            // `?? null` for logs written before handicaps existed, which have no
            // such field at all.
            s.handicap = event.handicap ?? null;
            // Put the spotted points on the board straight away. `frame.started`
            // does the same for every frame after this one, but a board that shows
            // the first frame before its own start event should not flash 0-0.
            s.scores = handicapScores(event.handicap ?? null);
            s.startedAt = event.at;
            s.matchWinner = null;
            s.status = 'in_progress';
            break;
        }
        case 'frame.started': {
            s.frameNumber = event.frameNumber;
            resetFrame(s, event.startingPlayer);
            break;
        }
        case 'shot.pot': {
            if (prev.paused)
                break;
            const p = s.practice ? 0 : event.player;
            s.activePlayer = p;
            // A nomination only counts while a free ball is actually in hand. A
            // stray flag on a replayed log must not invent points.
            const free = event.freeBall === true && prev.freeBall;
            const gained = free
                ? freeBallValue(prev, event.color)
                : BALL_VALUES[event.color] * event.count;
            s.scores[p] += gained;
            s.currentBreak += gained;
            if (s.currentBreak > s.highestBreak[p]) {
                s.highestBreak[p] = s.currentBreak;
            }
            const visit = openVisit(s, p);
            visit.entries.push({
                kind: 'pot',
                color: event.color,
                count: event.count,
                points: gained,
                ...(free ? { freeBall: true } : {}),
            });
            visit.points += gained;
            if (s.practice)
                break;
            if (free) {
                // The nominated ball was never the ball on, so it goes back on its
                // spot and nothing leaves the table: no red is struck off and no
                // colour is cleared. What the striker owes next is whatever they
                // would have owed had they potted the ball on itself.
                if (prev.mustPotColor) {
                    s.mustPotColor = false;
                }
                else if (prev.redsRemaining > 0) {
                    // It counted as a red, so a colour is now owed — and the red it
                    // stood in for is still on the table.
                    s.mustPotColor = true;
                }
                // Clearance: the ball on is still on, so nothing changes at all.
            }
            else if (event.color === 'red') {
                s.redsRemaining = Math.max(0, s.redsRemaining - event.count);
                s.mustPotColor = true;
            }
            else if (s.redsRemaining > 0 || s.mustPotColor) {
                // A colour potted while reds are still on (or the free colour after
                // the final red) is re-spotted, so it stays in colorsRemaining.
                s.mustPotColor = false;
            }
            else {
                // Clearance phase: the colour stays down.
                s.colorsRemaining = s.colorsRemaining.filter((c) => c !== event.color);
                s.mustPotColor = false;
            }
            // Spent, taken or declined, the free ball lasts one stroke.
            s.freeBall = false;
            s.freeBallDecisionPending = false;
            break;
        }
        case 'shot.miss': {
            if (prev.paused)
                break;
            if (s.practice) {
                endBreak(s);
                s.activePlayer = 0;
                break;
            }
            s.activePlayer = event.player;
            switchTurn(s);
            break;
        }
        case 'shot.foul': {
            if (prev.paused)
                break;
            if (s.practice) {
                endBreak(s);
                s.activePlayer = 0;
                break;
            }
            s.activePlayer = event.player;
            s.scores[opponent(event.player)] += event.points;
            // The foul is recorded against the player who committed it, which is
            // where somebody reading the history would look for it.
            openVisit(s, event.player).entries.push({ kind: 'foul', points: event.points });
            if (event.offenderPlaysAgain) {
                endBreak(s);
                s.mustPotColor = false;
                s.freeBallDecisionPending = false;
            }
            else {
                switchTurn(s);
                // Manual referee decision: after a foul that hands the table over,
                // the incoming striker is asked whether this is a free ball.
                s.freeBallDecisionPending = true;
            }
            // Free ball is a referee call, not an automatic consequence of a foul.
            // The event keeps its flag for backward compatibility with older logs,
            // but the state only changes on `freeball.awarded`.
            s.freeBall = false;
            if (event.restoration && event.offenderPlaysAgain) {
                s.mustPotColor = prev.mustPotColor;
                s.paused = true;
                s.resetOverlay = false;
                s.pauseReason = 'foul_and_miss';
                s.pauseMessage = null;
            }
            break;
        }
        case 'freeball.awarded': {
            if (prev.paused)
                break;
            // Only for the player actually at the table. A free ball awarded to
            // somebody sitting down would arm the ball pad for the wrong striker,
            // and this event can arrive from a recogniser as well as from a hand.
            if (event.player === s.activePlayer && s.status === 'in_progress') {
                s.freeBall = event.awarded;
                s.freeBallDecisionPending = false;
            }
            break;
        }
        case 'reds.removed': {
            if (prev.paused)
                break;
            s.redsRemaining = Math.max(0, s.redsRemaining - event.count);
            break;
        }
        case 'reds.adjusted': {
            if (illegalRedAdjustmentReason(prev, event.delta) !== null)
                break;
            s.redsRemaining += event.delta;
            break;
        }
        case 'score.corrected': {
            s.scores[event.player] = event.score;
            break;
        }
        case 'score.added': {
            // Replay preserves entries accepted before the submission limit became 147.
            if (illegalScoreAdditionReason(prev, event.player, event.points, 999) !== null)
                break;
            if (s.activePlayer !== event.player) {
                endBreak(s);
                s.activePlayer = event.player;
                s.mustPotColor = false;
                s.freeBall = false;
                s.freeBallDecisionPending = false;
            }
            s.scores[event.player] += event.points;
            s.currentBreak += event.points;
            s.highestBreak[event.player] = Math.max(s.highestBreak[event.player], s.currentBreak);
            const visit = openVisit(s, event.player);
            visit.entries.push({ kind: 'points', points: event.points });
            visit.points += event.points;
            if (event.endTurn)
                switchTurn(s);
            break;
        }
        case 'turn.switched': {
            if (prev.paused)
                break;
            s.activePlayer = event.player;
            s.mustPotColor = false;
            s.freeBall = false;
            s.freeBallDecisionPending = false;
            endBreak(s);
            break;
        }
        case 'mode.changed': {
            s.mode = event.mode;
            break;
        }
        case 'streaming.changed': {
            s.streaming = event.enabled;
            break;
        }
        case 'table.paused': {
            if (s.status === 'in_progress') {
                s.paused = true;
                s.resetOverlay = event.resetOverlay;
                s.pauseReason = event.reason;
                s.pauseMessage = event.message ?? null;
            }
            break;
        }
        case 'table.resumed': {
            s.paused = false;
            s.resetOverlay = false;
            s.pauseReason = null;
            s.pauseMessage = null;
            break;
        }
        case 'frame.ended': {
            endBreak(s);
            s.paused = false;
            s.resetOverlay = false;
            s.pauseReason = null;
            s.pauseMessage = null;
            s.frameResults = [
                ...s.frameResults,
                {
                    frameNumber: s.frameNumber,
                    scores: [...s.scores],
                    winner: event.winner,
                },
            ];
            s.framesWon[event.winner] += 1;
            // The match ending is a consequence of the frame count, not a separate
            // decision. Deriving it here means one undo reverses both.
            if (s.framesWon[event.winner] >= s.framesToWin) {
                s.status = 'match_over';
                s.matchWinner = event.winner;
                s.streaming = false;
                s.mode = 'manual';
            }
            else {
                s.status = 'frame_over';
            }
            break;
        }
        case 'match.ended': {
            if (event.reason === 'inactivity') {
                if (s.status !== 'in_progress' && s.status !== 'frame_over')
                    break;
                s.idleClosure = { at: event.at, status: s.status };
                s.streaming = false;
                s.mode = 'manual';
                s.status = 'match_over';
                s.matchWinner = null;
                break;
            }
            s.idleClosure = null;
            // Explicit end: a concession, walkover or retirement, which can happen
            // before either player has enough frames.
            endBreak(s);
            s.paused = false;
            s.resetOverlay = false;
            s.pauseReason = null;
            s.pauseMessage = null;
            // A finished table must not keep broadcasting or accepting recogniser
            // events. Keeping these effects on the match-ended event also makes undo
            // restore the switches exactly as they were before the end.
            s.streaming = false;
            s.mode = 'manual';
            s.status = 'match_over';
            s.matchWinner = event.winner ?? null;
            break;
        }
        case 'match.resumed': {
            if (!s.idleClosure || event.closedAt !== s.idleClosure.at)
                break;
            s.idleDurationMs += Math.max(0, Date.parse(event.at) - Date.parse(s.idleClosure.at));
            s.status = s.idleClosure.status;
            s.idleClosure = null;
            s.matchWinner = null;
            s.streaming = false;
            s.mode = 'manual';
            break;
        }
        case 'event.retracted':
            // Handled by `replay`, which filters the log before applying.
            break;
    }
    return derive(s);
}
/**
 * Rebuild the score from the event log.
 *
 * Undo appends an `event.retracted` rather than deleting, so the log stays
 * append-only and every state is reproducible from history.
 */
export function replay(matchId, events) {
    const retracted = new Set();
    let lastSeq = -1;
    for (const e of events) {
        lastSeq = Math.max(lastSeq, e.seq);
        if (e.type === 'event.retracted')
            retracted.add(e.targetSeq);
    }
    // Only active corrections suppress the AI stroke. Undoing the referee
    // correction restores the original stroke and its table state.
    for (const e of events) {
        if (e.type === 'shot.foul' && e.restoration && !retracted.has(e.seq)) {
            for (const seq of e.restoration.replacedSeqs)
                retracted.add(seq);
        }
    }
    let state = initialState(matchId);
    for (const e of events) {
        if (e.type === 'event.retracted')
            continue;
        if (retracted.has(e.seq))
            continue;
        state = applyEvent(state, e);
    }
    // Retractions rewind scoring, not the revision used to order state updates.
    state.lastSeq = lastSeq;
    return state;
}
export function toOsd(s) {
    return {
        p1: s.players[0],
        p2: s.players[1],
        c1: s.playerCountries[0],
        c2: s.playerCountries[1],
        s1: s.scores[0],
        s2: s.scores[1],
        remaining: s.pointsAvailable,
        active: s.activePlayer,
        break: s.currentBreak,
        frames: [...s.framesWon],
        toWin: s.framesToWin,
        won: s.matchWinner,
    };
}
//# sourceMappingURL=state.js.map